import React from "react";
import { Phone, Mail, Send, Paperclip, X, User, Users, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { SectionCard, EmptyState } from "./AdminUI";

const QUICK_TEMPLATES = [
  {
    label: "Rappel RDV",
    subject: "Rappel de rendez-vous - Auvergne Tech Racing",
    message: "Bonjour,\n\nNous vous rappelons votre rendez-vous prévu prochainement chez Auvergne Tech Racing.\n\nN'hésitez pas à nous contacter si vous avez des questions.\n\nCordialement,\nL'équipe Auvergne Tech Racing",
  },
  {
    label: "Véhicule prêt",
    subject: "Votre véhicule est prêt - Auvergne Tech Racing",
    message: "Bonjour,\n\nNous avons le plaisir de vous informer que les travaux sur votre véhicule sont terminés.\n\nVous pouvez venir le récupérer à votre convenance pendant nos horaires d'ouverture.\n\nCordialement,\nL'équipe Auvergne Tech Racing",
  },
  {
    label: "Devis disponible",
    subject: "Devis disponible - Auvergne Tech Racing",
    message: "Bonjour,\n\nSuite à votre demande, nous avons préparé un devis pour votre véhicule.\n\nN'hésitez pas à nous contacter pour en discuter ou prendre rendez-vous.\n\nCordialement,\nL'équipe Auvergne Tech Racing",
  },
  {
    label: "Information",
    subject: "Information importante - Auvergne Tech Racing",
    message: "Bonjour,\n\n",
  },
];

const MessagesTab = ({ ctx }) => {
  const {
    uniqueClients, selectedClient, selectClientForEmail, setSelectedClient,
    directEmailForm, setDirectEmailForm, emailFileInputRef, handleEmailFileSelect,
    emailAttachment, removeEmailAttachment, handleSendDirectEmail, sendingEmail,
  } = ctx;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Client List */}
      <div className="lg:col-span-1 space-y-5">
        <SectionCard title={`Clients (${uniqueClients.length})`} icon={Users}>
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1 -mr-1">
            {uniqueClients.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-4">Aucun client</p>
            ) : (
              uniqueClients.map((client, idx) => {
                const active = selectedClient?.client_email === client.client_email;
                return (
                  <button
                    key={idx}
                    onClick={() => selectClientForEmail(client)}
                    className={`w-full text-left p-3 rounded-lg transition-all border ${
                      active
                        ? "bg-cyan-500/10 border-cyan-500/30"
                        : "bg-[#1C1C1E] hover:bg-white/[0.04] border-white/[0.04]"
                    }`}
                  >
                    <p className="font-medium text-white text-sm truncate">{client.client_name}</p>
                    <p className="text-xs text-white/50 truncate mt-0.5">{client.client_email}</p>
                    <p className="text-[11px] text-white/35 mt-0.5">{client.client_phone}</p>
                  </button>
                );
              })
            )}
          </div>
        </SectionCard>

        <SectionCard title="Saisie manuelle" icon={User}>
          <div className="space-y-2.5">
            <Input
              type="text"
              value={directEmailForm.to_name}
              onChange={(e) => setDirectEmailForm({ ...directEmailForm, to_name: e.target.value })}
              placeholder="Nom du destinataire"
              className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
            />
            <Input
              type="email"
              value={directEmailForm.to_email}
              onChange={(e) => setDirectEmailForm({ ...directEmailForm, to_email: e.target.value })}
              placeholder="email@destinataire.com"
              className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
            />
          </div>
        </SectionCard>
      </div>

      {/* Composer */}
      <div className="lg:col-span-2">
        <SectionCard title="Composer un message" icon={Mail} iconColor="text-cyan-400">
          {directEmailForm.to_email && (
            <div className="mb-4 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
              <p className="text-[11px] uppercase tracking-wider text-white/50">Destinataire</p>
              <p className="text-cyan-400 font-medium mt-0.5">
                {directEmailForm.to_name || "Client"} &lt;{directEmailForm.to_email}&gt;
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Sujet</label>
              <Input
                type="text"
                value={directEmailForm.subject}
                onChange={(e) => setDirectEmailForm({ ...directEmailForm, subject: e.target.value })}
                placeholder="Sujet de votre message"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </div>

            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Message</label>
              <textarea
                value={directEmailForm.message}
                onChange={(e) => setDirectEmailForm({ ...directEmailForm, message: e.target.value })}
                placeholder="Écrivez votre message ici..."
                rows={10}
                className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-sm p-3 rounded-md resize-none focus:outline-none focus:border-cyan-400/50"
              />
            </div>

            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Pièce jointe</label>
              <input
                type="file"
                ref={emailFileInputRef}
                onChange={handleEmailFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                className="hidden"
              />
              {emailAttachment ? (
                <div className="flex items-center gap-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md">
                  <Paperclip size={16} className="text-emerald-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-emerald-400 font-medium text-sm truncate">{emailAttachment.name}</p>
                    <p className="text-white/40 text-xs">{(emailAttachment.size / 1024).toFixed(1)} Ko</p>
                  </div>
                  <button onClick={removeEmailAttachment} className="p-1 hover:bg-red-500/20 rounded transition-colors">
                    <X size={15} className="text-red-400" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => emailFileInputRef.current?.click()}
                  className="w-full p-3 border border-dashed border-white/[0.1] rounded-md hover:border-cyan-400/30 hover:bg-cyan-400/[0.03] transition-all flex items-center justify-center gap-2 text-white/50 hover:text-cyan-400 text-sm"
                >
                  <Paperclip size={15} />
                  Ajouter une pièce jointe (PDF, DOC, image...)
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setDirectEmailForm({ to_email: "", to_name: "", subject: "", message: "" });
                  setSelectedClient(null);
                  removeEmailAttachment && removeEmailAttachment();
                }}
                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-white rounded-md text-sm transition-colors"
              >
                Effacer
              </button>
              <button
                onClick={handleSendDirectEmail}
                disabled={sendingEmail || !directEmailForm.to_email}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-500/30 disabled:cursor-not-allowed text-white py-2 px-4 rounded-md text-sm transition-colors font-semibold flex items-center justify-center gap-2"
              >
                {sendingEmail ? "Envoi en cours..." : (
                  <><Send size={15} /> Envoyer{emailAttachment ? " avec pièce jointe" : ""}</>
                )}
              </button>
            </div>
          </div>

          {/* Quick templates */}
          <div className="mt-5 pt-4 border-t border-white/[0.06]">
            <p className="text-white/50 text-[11px] uppercase tracking-wider mb-2.5">Messages rapides</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  onClick={() => setDirectEmailForm({ ...directEmailForm, subject: t.subject, message: t.message })}
                  className="px-3 py-1.5 bg-[#1C1C1E] hover:bg-white/[0.06] text-white/70 hover:text-white text-xs rounded-md border border-white/[0.06] transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default MessagesTab;
